import {
    Component,
    forwardRef,
    Input,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

// SESSION-admin-ptg-004 — shared `.mem-picker` control. Renders a scrollable
// checkable list of workspace users (sm avatar + name + meta sub-line). Two-way
// binds to `string[]` via ngModel / formControlName. Reused by the New-team and
// New-group dialogs (edit-members modes share the same control with their dto
// pre-populated). Mirrors prototype/app/teams.html#newTeamModal `.mem-picker`.

export interface MemberOption {
    id: string;
    name: string;
    meta?: string;
    /** Avatar tint — 'purple' (IS), 'coral' (FS), 'teal' (CRO), 'amber' (Admin). */
    tint?: 'purple' | 'coral' | 'teal' | 'amber' | 'neutral';
    initials?: string;
    /** SPEC-web-planner-013 AC-91: shown ticked and cannot be toggled (the team lead is always a member). */
    locked?: boolean;
}

@Component({
    selector: 'app-member-picker',
    templateUrl: './member-picker.component.html',
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MemberPickerComponent),
            multi: true,
        },
    ],
})
export class MemberPickerComponent implements ControlValueAccessor {
    @Input() options: MemberOption[] = [];
    /** Optional copy shown when the option list is empty. */
    @Input() emptyHint = 'No workspace members yet.';

    value: string[] = [];

    private onChange: (v: string[]) => void = () => {};
    private onTouched: () => void = () => {};

    writeValue(v: string[]): void {
        this.value = Array.isArray(v) ? [...v] : [];
    }

    registerOnChange(fn: (v: string[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    isChecked(id: string): boolean {
        return this.value.includes(id) || !!this.options.find((o) => o.id === id)?.locked;
    }

    toggle(id: string): void {
        if (this.options.find((o) => o.id === id)?.locked) {
            return;
        }
        const next = [...this.value];
        const i = next.indexOf(id);
        if (i >= 0) {
            next.splice(i, 1);
        } else {
            next.push(id);
        }
        this.value = next;
        this.onChange(this.value);
        this.onTouched();
    }

    trackById(_index: number, opt: MemberOption): string {
        return opt.id;
    }

    deriveInitials(opt: MemberOption): string {
        if (opt.initials) {
            return opt.initials;
        }
        const parts = (opt.name || '').trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) {
            return '?';
        }
        if (parts.length === 1) {
            return parts[0].substring(0, 2).toUpperCase();
        }
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }

    avatarClass(opt: MemberOption): string {
        const t = opt.tint || 'neutral';
        return t === 'neutral' ? '' : `avatar-${t}`;
    }
}
